import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import { UI } from "@wwimmo/ui";
import { useSelectionListKeyHandler } from "src/hooks/ticket/selection-list-key-handler/useSelectionListKeyHandler";
import { boldenMatchedSubstring } from "src/utils/Common";
import { ErpType } from "src/network/User";
import styles from "./RealestateVisaUserField.module.css";
import { IUserToAssign } from "src/stores/krediflow/AssignInvoiceStore";
import { STATE } from "src/stores/krediflow/RealestateVisaStore";

interface IRealestateVisaUserProps {
    realestateId: string;
    visaNumber: number;
}

const RealestateVisaUserFieldBase = (props: IRealestateVisaUserProps) => {
    const { t } = useTranslation();
    const { realestateVisaStore, invoiceStore, authStore } = useContext(RootStoreContext);

    const { realestateId, visaNumber } = props;

    const [displayRealestateVisaUserList, setDisplayRealestateVisaUserList] = useState<boolean>(false);
    const [lastScrollPositionYRealestateVisaUserItems, setLastSrollYPositionRealestateVisaUserItems] =
        useState<number>(0);

    const realestateVisaUserInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (
            realestateVisaStore.triggerFocusNextInput &&
            realestateVisaStore.triggerFocusNextInput.isActive &&
            realestateVisaStore.triggerFocusNextInput.realestateId === realestateId &&
            realestateVisaStore.triggerFocusNextInput.visaNumber === visaNumber
        ) {
            realestateVisaUserInputRef.current?.focus();
            realestateVisaStore.setTriggerFocusNextInput(false, "", 0);
        }
        // This useEffect only listens to changes in realestateVisaStore.triggerFocusNextInput
        // eslint-disable-next-line
    }, [realestateVisaStore.triggerFocusNextInput]);

    const resetSearchQuery = useCallback(() => {
        const currentSelectedRealestateVisaUserQueryString =
            realestateVisaStore.getSelectedRealestateVisaUserQueryString(realestateId, visaNumber);

        realestateVisaStore.setCurrentSearchQuery(
            realestateId,
            visaNumber,
            currentSelectedRealestateVisaUserQueryString ?? ""
        );
    }, [realestateId, realestateVisaStore, visaNumber]);

    useEffect(() => {
        const handleClickOutsideRealestateVisaUserAccountInput = (event: any) => {
            const target = event.target as HTMLElement;

            const isClickOutsideRealestateVisaUserInputOrList = () => {
                if (!displayRealestateVisaUserList || !realestateVisaUserInputRef.current) {
                    return true;
                }

                const clickedInsideInput = realestateVisaUserInputRef.current.contains(target);

                const clickedDropdownList =
                    target.classList.contains("dropdown-list") && target.classList.contains("realestate-visa-user");

                const clickedListItem =
                    target.classList.contains("list-item") && target.classList.contains("realestate-visa-user");

                const clickedIcon = target.localName === "path" || target.localName === "svg";

                const clickedHighlightedText =
                    target.classList.contains("highlighted-text") && target.classList.contains("realestate-visa-user");

                const clickedListItemChildElement =
                    target.parentElement?.classList.contains("list-item") &&
                    target.parentElement.classList.contains("realestate-visa-user");

                return !(
                    clickedDropdownList ||
                    clickedInsideInput ||
                    clickedListItem ||
                    clickedListItemChildElement ||
                    clickedIcon ||
                    clickedHighlightedText
                );
            };

            if (isClickOutsideRealestateVisaUserInputOrList()) {
                setDisplayRealestateVisaUserList(false);

                if (
                    realestateVisaStore.isRealestateVisaUserSelected(realestateId, visaNumber) &&
                    realestateVisaStore.getCurrentRealestateVisaUserSearchQuery(realestateId, visaNumber) === ""
                ) {
                    realestateVisaStore.resetRealestateVisaForVisaRole(realestateId, visaNumber);
                } else {
                    resetSearchQuery();
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutsideRealestateVisaUserAccountInput);

        return () => {
            document.removeEventListener("mousedown", handleClickOutsideRealestateVisaUserAccountInput);
        };
    }, [displayRealestateVisaUserList, resetSearchQuery, realestateId, realestateVisaStore, visaNumber]);

    const updateDataWithSelectedRealestateVisaUser = useCallback(
        async (userToAssign: IUserToAssign, realestateId: string, visaNumber: number) => {
            realestateVisaStore.updateDataWithSelectedRealestateVisaUser(userToAssign, realestateId, visaNumber);
        },
        [realestateVisaStore]
    );

    const onSelectAccountItem = useCallback(
        (activeListItemIndex: number) => {
            const selectedRealestateVisaUser = realestateVisaStore.filteredRealestateVisaUserData(
                realestateId,
                visaNumber
            )[activeListItemIndex];

            updateDataWithSelectedRealestateVisaUser(selectedRealestateVisaUser, realestateId, visaNumber);
        },
        [updateDataWithSelectedRealestateVisaUser, realestateVisaStore, realestateId, visaNumber]
    );

    const onTabFromRealestateVisaUserField = useCallback(() => {
        const usersToAssignInList = realestateVisaStore.filteredRealestateVisaUserData(realestateId, visaNumber);
        if (usersToAssignInList.length && usersToAssignInList.length === 1) {
            const onlyRealestateVisaUserList = usersToAssignInList[0];
            updateDataWithSelectedRealestateVisaUser(onlyRealestateVisaUserList, realestateId, visaNumber);
        }

        // TODO Implement Trigger
        if (visaNumber !== 3) {
            realestateVisaStore.setTriggerFocusNextInput(true, realestateId, visaNumber + 1);
        }
    }, [updateDataWithSelectedRealestateVisaUser, realestateId, visaNumber, realestateVisaStore]);

    const { onKeyDownFunction, setActiveListItemIndex, NO_LIST_ITEM_SELECTED } = useSelectionListKeyHandler({
        listName: "realestate-visa-user",
        totalNumberOfDisplayedItems: invoiceStore.numberOfDisplayedItems,
        setIsListDisplayed: setDisplayRealestateVisaUserList,
        isListDisplayed: displayRealestateVisaUserList,
        onSelectItem: onSelectAccountItem,
        resetSearchQuery: resetSearchQuery,
        additionalTabActionWithNoItemSelected: onTabFromRealestateVisaUserField
    });

    const resetAmountOfItemsDisplayedInRealestateVisaUserList = useCallback(() => {
        setLastSrollYPositionRealestateVisaUserItems(0);
        invoiceStore.setNumberOfDisplayedItems(invoiceStore.sliceSizeOfDisplayedItems);
    }, [invoiceStore]);

    const onChangeRealestateVisaUserInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            resetAmountOfItemsDisplayedInRealestateVisaUserList();

            const inputRealestateVisaUser = e.target.value;
            realestateVisaStore.setCurrentSearchQuery(realestateId, visaNumber, inputRealestateVisaUser);
        },
        [resetAmountOfItemsDisplayedInRealestateVisaUserList, realestateId, realestateVisaStore, visaNumber]
    );

    const onFocusRealestateVisaUserInput = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (
                realestateVisaStore.isRealestateVisaUserSelected(realestateId, visaNumber) &&
                realestateVisaUserInputRef.current
            ) {
                realestateVisaUserInputRef.current.select();
            }

            resetAmountOfItemsDisplayedInRealestateVisaUserList();
            setDisplayRealestateVisaUserList(true);
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
        },
        [
            resetAmountOfItemsDisplayedInRealestateVisaUserList,
            setActiveListItemIndex,
            NO_LIST_ITEM_SELECTED,
            realestateId,
            realestateVisaStore,
            visaNumber
        ]
    );

    const onClickRealesteVisaUserItem = useCallback(
        (realestateVisaUser: IUserToAssign, realestateId: string, visaNumber: number) => async (e: any) => {
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
            setDisplayRealestateVisaUserList(false);

            updateDataWithSelectedRealestateVisaUser(realestateVisaUser, realestateId, visaNumber);
        },
        [setActiveListItemIndex, NO_LIST_ITEM_SELECTED, updateDataWithSelectedRealestateVisaUser]
    );

    const onScrollRealestateVisaUserList = useCallback(
        (e: React.BaseSyntheticEvent) => {
            const windowHeight = e.target.clientHeight;
            const pixelsFromTop = e.target.scrollTop;
            const totalHeight = e.target.scrollHeight;

            const is150PixelsFromBottom = totalHeight - pixelsFromTop < windowHeight + 150;
            const isScrollingDown = pixelsFromTop > lastScrollPositionYRealestateVisaUserItems;

            const hasMoreResulsToDisplay =
                invoiceStore.numberOfDisplayedItems <
                realestateVisaStore.filteredRealestateVisaUserData(realestateId, visaNumber).length;

            if (is150PixelsFromBottom && isScrollingDown && hasMoreResulsToDisplay) {
                invoiceStore.setNumberOfDisplayedItems(
                    invoiceStore.numberOfDisplayedItems + invoiceStore.sliceSizeOfDisplayedItems
                );
            }

            setLastSrollYPositionRealestateVisaUserItems(pixelsFromTop);
        },
        [lastScrollPositionYRealestateVisaUserItems, invoiceStore, realestateVisaStore, realestateId, visaNumber]
    );

    const realestateVisaUserSelectionList = (
        <ul
            id="realestate-visa-user-dropdown-list"
            className={`dropdown-list realestate-visa-user krediflow ${
                authStore.user?.erpType === ErpType.IT2 ? "IT2" : "RIMO"
            }`}
            onScroll={onScrollRealestateVisaUserList}
        >
            {realestateVisaStore.currentState === STATE.LOADING_DATA ? (
                <li className="d-flex align-items-center justify-content-center list-item realestate-visa-user loading">
                    <div>
                        <UI.RotatingSpinner noLogo size={20} />
                    </div>
                </li>
            ) : (
                realestateVisaStore
                    .filteredRealestateVisaUserData(realestateId, visaNumber)
                    .slice(0, invoiceStore.numberOfDisplayedItems)
                    .map((realestateVisaUser, index) => {
                        const realestateVisaUserName = realestateVisaUser.name;

                        const currentRealestateVisaUserSearchQuery =
                            realestateVisaStore.getCurrentRealestateVisaUserSearchQuery(realestateId, visaNumber);

                        const formattedRealestateVisaUserName = boldenMatchedSubstring(
                            currentRealestateVisaUserSearchQuery ?? "",
                            realestateVisaUserName,
                            "highlighted-text realestate-visa-user"
                        );

                        return (
                            <li
                                key={index}
                                className="d-flex align-items-center list-item realestate-visa-user"
                                onClick={onClickRealesteVisaUserItem(realestateVisaUser, realestateId, visaNumber)}
                                id={`realestate-visa-user-list-item-${index}`}
                            >
                                <div
                                    className={`realestate-visa-user-text ${styles.RealestateVisaUserText}`}
                                    dangerouslySetInnerHTML={{
                                        __html: formattedRealestateVisaUserName
                                    }}
                                />
                            </li>
                        );
                    })
            )}
        </ul>
    );

    return (
        <>
            <UI.Input
                ref={realestateVisaUserInputRef}
                className={styles.RealestateUserInput}
                autoComplete="off"
                placeholder={`${t("screens.kredi_flow.visa")} ${visaNumber}`}
                type="text"
                value={realestateVisaStore.getCurrentRealestateVisaUserSearchQuery(realestateId, visaNumber) ?? ""}
                onChange={onChangeRealestateVisaUserInput}
                onFocus={onFocusRealestateVisaUserInput}
                onKeyDown={onKeyDownFunction}
                iconRight={<UI.Icon icon={UI.SVGIcon.Search} color="grey" size={"small"} style={{ right: "5px" }} />}
            />
            {displayRealestateVisaUserList ? realestateVisaUserSelectionList : undefined}
        </>
    );
};

export const RealestateVisaUserField = Sentry.withProfiler(observer(RealestateVisaUserFieldBase));
